import { ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { Button, Card, Checkbox, CheckboxOptionType, Col, Dropdown, Form, Input, InputNumber, Row, Select } from 'antd';
import React from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { GUID, ServiceFeePayingEntityType, SystemAppType } from '@/constants';
import CompanyInput from '@/components/FormItems/CompanyInput';
import { FormInstance } from 'rc-field-form';
import { orderBy } from 'lodash';
import { FeePayingEntityModel } from '@/__generated';

type Props = {
  availableApps: number[] | null | undefined;
  form: FormInstance<any>;
};
export const parseFeePayingEntitiesData = (entities: FeePayingEntityModel[]) => {
  return orderBy(entities, x =>
    ServiceFeePayingEntityType.__orderedKey.indexOf(ServiceFeePayingEntityType.__getValue(x.serviceFeePayingEntityType)),
  ).map(entity => {
    return { ...entity, canDelete: entities.filter(x => x.serviceFeePayingEntityType === entity.serviceFeePayingEntityType).length > 1 };
  });
};
export default function FeePayingEntities({ availableApps, form }: Props) {
  const { t } = useTranslation();

  const feePaying: number[] = Form.useWatch('feePaying', form);

  const feePayingOptions: CheckboxOptionType[] = React.useMemo(() => {
    return ServiceFeePayingEntityType.__getSelectOptions().filter(op => {
      if (op.value === ServiceFeePayingEntityType.Customer) {
        return true;
      } else if (op.value === ServiceFeePayingEntityType.Merchant) {
        return availableApps?.includes(SystemAppType.ConektoRetail);
      } else if (op.value === ServiceFeePayingEntityType.MonektoUsers) {
        return availableApps?.includes(SystemAppType.Monekto);
      } else if (op.value === ServiceFeePayingEntityType.TransportCompanies) {
        return availableApps?.includes(SystemAppType.ConektoTransport);
      }
      return false;
    });
  }, [availableApps]);

  const feePayingAddOptions = React.useMemo(() => {
    return feePaying
      ?.filter(x => x !== ServiceFeePayingEntityType.Customer)
      .map(f => {
        return {
          key: f,
          label: ServiceFeePayingEntityType.__getValue(f),
        };
      });
  }, [feePaying]);
  React.useEffect(() => {
    let feePayingEntities: FeePayingEntityModel[] = form.getFieldValue('feePayingEntities') || [];
    if (feePayingEntities) {
      feePayingEntities = feePayingEntities.filter(entity => {
        return feePayingOptions.some(option => option.value === (entity.serviceFeePayingEntityType as any));
      });
      form.setFieldValue('feePayingEntities', parseFeePayingEntitiesData(feePayingEntities));
    }
  }, [feePayingOptions]);

  React.useEffect(() => {
    let feePayingEntities: FeePayingEntityModel[] = form.getFieldValue('feePayingEntities') || [];
    if (feePaying) {
      feePayingEntities = feePayingEntities.filter(x => feePaying.includes(x.serviceFeePayingEntityType as any));
      feePaying.forEach(f => {
        if (!feePayingEntities.some(x => f === (x.serviceFeePayingEntityType as any))) {
          feePayingEntities.push({
            rate: 0,
            serviceFeePayingEntityType: f as any,
            isPercentageBase: true,
          });
        }
      });
      form.setFieldValue('feePayingEntities', parseFeePayingEntitiesData(feePayingEntities));
    }
  }, [feePaying]);
  const onAddFeePayingEntities = (type: any) => {
    let feePayingEntities: FeePayingEntityModel[] = form.getFieldValue('feePayingEntities') || [];
    feePayingEntities.push({
      isPercentageBase: true,
      rate: 0,
      serviceFeePayingEntityType: type,
    });
    form.setFieldValue('feePayingEntities', parseFeePayingEntitiesData(feePayingEntities));
  };
  const onDeleteFeePayingEntities = () => {
    let feePayingEntities: FeePayingEntityModel[] = form.getFieldValue('feePayingEntities') || [];
    form.setFieldValue('feePayingEntities', parseFeePayingEntitiesData(feePayingEntities));
  };
  return (
    <Card
      size="small"
      title={<b>{t('servicePages_FeePayingEntities')}</b>}
      style={{ backgroundColor: '#f5f5f5' }}
      extra={
        feePayingAddOptions?.length ? (
          <Dropdown
            menu={{
              items: feePayingAddOptions as any,
              onClick: item => {
                onAddFeePayingEntities(parseInt(item.key as any));
              },
            }}
            placement="bottomLeft"
          >
            <Button size="small" type="primary" icon={<PlusOutlined />} />
          </Dropdown>
        ) : null
      }
    >
      <Form.Item name="feePaying">
        <Checkbox.Group options={feePayingOptions} />
      </Form.Item>
      <Form.List name="feePayingEntities">
        {(fields, { remove }) => {
          return (
            <div>
              {fields.map((field, index) => {
                return (
                  <Row gutter={ROW_GUTTER} key={field.key}>
                    <Col md={2}>
                      <Form.Item name={[field.name, 'serviceFeePayingEntityType']} noStyle>
                        <Input type="hidden" />
                      </Form.Item>
                    </Col>
                    <Col md={10}>
                      <Form.Item noStyle dependencies={[['feePayingEntities', field.name, 'serviceFeePayingEntityType']]}>
                        {({ getFieldValue }) => {
                          const feeEntitytype = getFieldValue(['feePayingEntities', field.name, 'serviceFeePayingEntityType']);

                          if (feeEntitytype === ServiceFeePayingEntityType.Customer) {
                            return (
                              <>
                                <Form.Item name={[field.name, 'entityId']} noStyle>
                                  <Input type="hidden" />
                                </Form.Item>
                                <Form.Item label={t('servicePages_Customer')}>
                                  <Input value={'CUSTOMER'} disabled className="input-value-color" />
                                </Form.Item>
                              </>
                            );
                          } else if (feeEntitytype === ServiceFeePayingEntityType.Merchant) {
                            return (
                              <Form.Item
                                label={t('servicePages_Merchant')}
                                name={[field.name, 'entityId']}
                                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                              >
                                <Select>
                                  <Select.Option key={GUID.EMPTY}>{t('ALL')}</Select.Option>
                                </Select>
                              </Form.Item>
                            );
                          } else if (feeEntitytype === ServiceFeePayingEntityType.MonektoUsers) {
                            return (
                              <Form.Item
                                label={t('servicePages_MonektoUsers')}
                                name={[field.name, 'entityId']}
                                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                              >
                                <Select>
                                  <Select.Option key={GUID.EMPTY}>{t('ALL')}</Select.Option>
                                </Select>
                              </Form.Item>
                            );
                          } else if (feeEntitytype === ServiceFeePayingEntityType.TransportCompanies) {
                            return (
                              <Form.Item
                                label={t('servicePages_TransportCompanies')}
                                name={[field.name, 'entityId']}
                                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                              >
                                <CompanyInput allowSelectAll />
                              </Form.Item>
                            );
                          }
                          return null;
                        }}
                      </Form.Item>
                    </Col>
                    <Col md={4}>
                      <Form.Item name={[field.name, 'rate']} label={t('servicePages_Rate')} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <InputNumber min={0} />
                      </Form.Item>
                    </Col>
                    <Col md={7}>
                      <Form.Item label="  " name={[field.name, 'isPercentageBase']} valuePropName="checked">
                        <Checkbox>{t('servicePages_IsPercentageBase')}</Checkbox>
                      </Form.Item>
                    </Col>
                    <Col md={1} style={{ marginTop: 35 }}>
                      <Form.Item noStyle dependencies={[['feePayingEntities', field.name, 'canDelete']]}>
                        {({ getFieldValue }) => {
                          var canDelete = getFieldValue(['feePayingEntities', field.name, 'canDelete']);
                          if (canDelete) {
                            return (
                              <Button
                                size="small"
                                onClick={() => {
                                  remove(index);
                                  onDeleteFeePayingEntities();
                                }}
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                              />
                            );
                          } else {
                            return null;
                          }
                        }}
                      </Form.Item>
                    </Col>
                  </Row>
                );
              })}
            </div>
          );
        }}
      </Form.List>
    </Card>
  );
}
